import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';

// utils
import { fetcher } from 'utils/axios';
import axios from 'utils/axios';

// types
import { Item, CreateItemData, UpdateItemData, ItemTranslation, ItemPrice, FormattedItem, SystemUnit } from 'types/item';

const endpoints = {
  items: 'api/items',
  systemUnits: 'api/items/system-units'
};

// ============================================================================
// ITEMS
// ============================================================================

export function useGetItems(companyId?: number) {
  const { data, isLoading, error, isValidating } = useSWR(companyId ? `${endpoints.items}?company=${companyId}` : null, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      items: ((data?.results || data) as Item[]) || [],
      itemsLoading: isLoading,
      itemsError: error,
      itemsValidating: isValidating,
      itemsEmpty: !isLoading && !((data?.results || data) as Item[])?.length
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// Get single item
export async function getItem(itemId: number, companyId: number): Promise<Item> {
  try {
    const response = await axios.get(`${endpoints.items}/${itemId}/?company=${companyId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching item:', error);
    throw error;
  }
}

// Create item
export async function createItem(itemData: CreateItemData): Promise<Item> {
  try {
    const { company, ...bodyData } = itemData;
    const response = await axios.post(`${endpoints.items}/?company=${company}`, bodyData);

    // Revalidate the items list
    mutate(`${endpoints.items}?company=${company}`);

    return response.data;
  } catch (error) {
    console.error('Error creating item:', error);
    throw error;
  }
}

// Update item
export async function updateItem(itemData: UpdateItemData): Promise<Item> {
  try {
    const { company, id, ...bodyData } = itemData;
    const response = await axios.put(`${endpoints.items}/${id}/?company=${company}`, bodyData);

    // Revalidate the items list
    if (company) {
      mutate(`${endpoints.items}?company=${company}`);
    }

    return response.data;
  } catch (error) {
    console.error('Error updating item:', error);
    throw error;
  }
}

// Delete item
export async function deleteItem(itemId: number, companyId: number): Promise<void> {
  try {
    await axios.delete(`${endpoints.items}/${itemId}/?company=${companyId}`);

    // Revalidate the items list
    mutate(`${endpoints.items}?company=${companyId}`);
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
}

// Search items (for autocomplete)
export async function searchItems(
  companyId: number,
  searchQuery: string,
  filters?: {
    type?: 'material' | 'labor';
    language?: string;
    limit?: number;
  }
): Promise<{ results: Item[]; count: number }> {
  try {
    const params = new URLSearchParams({
      company: companyId.toString(),
      q: searchQuery
    });

    if (filters?.type) params.append('type', filters.type);
    if (filters?.language) params.append('language', filters.language);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await axios.get(`${endpoints.items}/search/?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching items:', error);
    throw error;
  }
}

// Get items by type
export async function getItemsByType(companyId: number, type: 'material' | 'labor'): Promise<Item[]> {
  try {
    const response = await axios.get(`${endpoints.items}/by_type/?company=${companyId}&type=${type}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching items by type:', error);
    throw error;
  }
}

// ============================================================================
// SYSTEM UNITS
// ============================================================================

// Get system units
export async function getSystemUnits(): Promise<SystemUnit[]> {
  try {
    const response = await axios.get(endpoints.systemUnits);
    return response.data;
  } catch (error) {
    console.error('Error fetching system units:', error);
    throw error;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getItemTranslation(item: Item, languageCode: string = 'hu'): ItemTranslation | undefined {
  // Try to find exact match
  let translation = item.translations.find((t) => t.language_code === languageCode);

  // Fallback to Hungarian if not found
  if (!translation && languageCode !== 'hu') {
    translation = item.translations.find((t) => t.language_code === 'hu');
  }

  // Last resort: return first translation
  if (!translation && item.translations.length > 0) {
    translation = item.translations[0];
  }

  return translation;
}

export function getItemPrice(item: Item, currencyCode: string = 'HUF'): ItemPrice | undefined {
  // Try to find exact match
  let price = item.prices.find((p) => p.currency_code === currencyCode);

  // Fallback to HUF if not found
  if (!price && currencyCode !== 'HUF') {
    price = item.prices.find((p) => p.currency_code === 'HUF');
  }

  // Last resort: return first price
  if (!price && item.prices.length > 0) {
    price = item.prices[0];
  }

  return price;
}

export function getItemDisplayName(item: Item, languageCode: string = 'hu'): string {
  const translation = getItemTranslation(item, languageCode);
  return translation?.name || `Item #${item.id}`;
}

export function formatItemForDisplay(item: Item, languageCode: string = 'hu', currencyCode: string = 'HUF'): FormattedItem {
  const translation = getItemTranslation(item, languageCode);
  const price = getItemPrice(item, currencyCode);

  return {
    id: item.id,
    name: translation?.name || `Item #${item.id}`,
    description: translation?.description || '',
    price: price?.price || '0',
    priceFormatted: price ? `${price.price} ${price.currency_code}` : '0',
    unit: item.unit_abbreviation,
    type: item.item_type,
    taxRate: item.default_tax_rate
  };
}
